'use client'
import React, {
  createContext,
  Dispatch,
  ReactNode,
  SetStateAction,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  useSyncExternalStore,
} from 'react'
import { useTranslations } from 'next-intl'
import { useQueryClient } from '@tanstack/react-query'
import { v7 as uuid } from 'uuid'

import {
  AdaptedProduct,
  Catalog,
  CatalogBlock,
  Context,
  UpdateBlockParams,
  UpdateBlockPayload,
} from '@/types'
import {
  useCatalogData,
  useCatalogKeyboardShortcuts,
  useCatalogNavigation,
  useCatalogState,
  useCatalogVersioning,
  useZoom,
} from '@/hooks'
import {
  type ProjectPermissions,
  resolveProjectPermissions,
} from '@/features/project/project.permissions'
import { sortBlocksByLotAndCategory } from '@/utils'
import { getCatalogRotorFooterLabel } from '@/utils/catalogMetadata'
import {
  buildContentFallbackLocales,
  normalizeCatalogLocale,
} from '@/utils/languagePolicy'
import {
  resolveCanonicalCatalogLanguageState,
  resolveEffectiveCatalogLanguageSettings,
  resolveProjectLanguageSettings,
} from '@/features/project/projectLanguageSettings'
import {
  getReducedProductFromCache,
  reconcileProductOccurrencesInCache,
} from '@/services/queries/productCache.utils'
import { productByID } from '@/services/queries/product'
import {
  catalogAdapter,
  pruneEmptyCatalogSections,
} from '@/services/queries/catalog'
import { useProject } from '@/services/queries/projects'
import { useUserContext } from './UserContext'
import { useSessionRuntime } from './SessionRuntimeContext'
import { serializeCatalogBlocks } from '@/utils/catalog.handler'
import {
  catalogBaseStamp,
  completeCatalogDraft,
  discardCatalogDraft,
  EMPTY_CATALOG_DRAFT_STATE,
  readCatalogDraftState,
  retainCatalogDraft,
} from './catalogEditorDraft'

const NEW_CATALOG_SELECTION_ID = '__catalog-editor-new__'

interface CatalogEditorContextType {
  // Data
  catalogs: Partial<Catalog>[]
  selectedCatalogId?: string
  blocks: CatalogBlock[]
  hasLoaded: boolean
  hasError: boolean
  isInitializing: boolean
  isHydratingProducts: boolean

  // Version control
  currentVersion: number
  hasUnsavedCatalogDraft: boolean
  hasPendingProductSync: boolean
  isSaving: boolean

  // Options management
  date?: string
  setDate: Dispatch<SetStateAction<string | undefined>>
  supportedLocales: string[]
  defaultLocale: string
  contentLocale: string
  setContentLocale: Dispatch<SetStateAction<string>>
  contentFallbackLocales: string[]
  projectDefaultLocale: string
  firstPageNumber: number
  setFirstPageNumber: Dispatch<SetStateAction<number>>

  // Rotor template - footer override
  rotorFooterLabel?: string
  setRotorFooterLabel: Dispatch<SetStateAction<string | undefined>>

  // Block operations
  updateBlock: ({ blockId, payload }: UpdateBlockParams) => void
  reorderBlocks: (newBlockIds: string[]) => void
  handleSort: () => void

  // Navigation
  selectedBlockId?: string
  categoryRefs: React.MutableRefObject<Record<string, HTMLDivElement>>
  selectBlock: (blockId?: string) => void
  clearSelectedBlock: () => void
  handleNavigate: (blockId: string) => void
  setVirtualizedScrollRef: (scrollRef: {
    scrollToIndex: (index: number, align?: 'start' | 'center' | 'end') => void
  }) => void
  isInspectorOpen: boolean
  openInspector: () => void
  closeInspector: () => void
  toggleInspector: () => void
  requestCatalogSwitch: (catalogId: string) => void
  confirmDiscardAndSwitch: (catalogId: string) => void

  // Zoom
  zoomRef: React.RefObject<HTMLDivElement | null>
  zoomIn: () => void
  zoomOut: () => void
  currentZoomLevel: number

  // Actions
  handleSave: () => void
  markAsChanged: () => void
  refreshBlocksByProductID: (productID: string) => void
  syncProductInCatalog: (productID: string) => Promise<void>
  commitProductTextInCatalog: (params: {
    product: AdaptedProduct
    patch: UpdateBlockPayload
    commit: (base: AdaptedProduct) => Promise<void>
  }) => Promise<void>
  beginProductSync: () => void
  endProductSync: () => void

  // Context
  catalogContext: Context | undefined
  catalogStructureContext?: Context | undefined
  projectID: string
  locale: string
  projectPermissions: ProjectPermissions
}

const CatalogEditorContext = createContext<
  CatalogEditorContextType | undefined
>(undefined)

type CatalogLanguageSettings = ReturnType<
  typeof resolveEffectiveCatalogLanguageSettings
>

interface CatalogEditorProviderProps {
  children: ReactNode
  projectID: string
  locale: string
}

const getPublishDateInputValue = (catalog?: Partial<Catalog>) => {
  if (catalog?.publishDate) {
    return new Date(catalog.publishDate).toISOString().split('T')[0]
  }
  return new Date().toISOString().split('T')[0]
}

const getFirstPageNumber = (catalog?: Partial<Catalog>) => {
  if (catalog?.firstPageNumber != null) {
    return catalog.firstPageNumber
  }

  const meta = catalog?.metadata?.find((item) => item.key === 'firstPageNumber')
  if (!meta?.value) return 1

  const parsed = Number.parseInt(meta.value, 10)
  return Number.isNaN(parsed) ? 1 : parsed
}

const applyCatalogLanguageSettings = ({
  languageSettings,
  supportedLocales,
  defaultLocale,
  contentLocale,
  setSupportedLocales,
  setDefaultLocale,
  setContentLocale,
}: {
  languageSettings: CatalogLanguageSettings
  supportedLocales: readonly string[]
  defaultLocale: string
  contentLocale: string
  setSupportedLocales: Dispatch<SetStateAction<string[]>>
  setDefaultLocale: Dispatch<SetStateAction<string>>
  setContentLocale: Dispatch<SetStateAction<string>>
}) => {
  const resolvedState = resolveCanonicalCatalogLanguageState({
    languageSettings,
    contentLocale,
  })
  const supportedSignature = supportedLocales.join('|')
  const resolvedSignature = resolvedState.supportedLocales.join('|')

  if (supportedSignature !== resolvedSignature) {
    setSupportedLocales(resolvedState.supportedLocales)
  }
  if (defaultLocale !== resolvedState.defaultLocale) {
    setDefaultLocale(resolvedState.defaultLocale)
  }
  if (contentLocale !== resolvedState.contentLocale) {
    setContentLocale(resolvedState.contentLocale)
  }
}

export function CatalogEditorProvider({
  children,
  projectID,
  locale,
}: CatalogEditorProviderProps) {
  const tCommon = useTranslations('Common')
  const { authorization, ownership, organizationScope, customSettings } =
    useUserContext()
  const { data: project } = useProject(projectID)
  const queryClient = useQueryClient()
  const { sessionScopeKey } = useSessionRuntime()
  const draftCacheKey = useMemo(() => {
    const organizationId = organizationScope.isSolo
      ? 'solo'
      : organizationScope.activeOrganizationId
    return sessionScopeKey !== 'public' && organizationId
      ? ['catalog-editor-draft', sessionScopeKey, organizationId, projectID]
      : undefined
  }, [
    sessionScopeKey,
    organizationScope.isSolo,
    organizationScope.activeOrganizationId,
    projectID,
  ])
  const subscribeToDrafts = useCallback(
    (onChange: () => void) => queryClient.getQueryCache().subscribe(onChange),
    [queryClient],
  )
  const getDraftState = useCallback(
    () =>
      draftCacheKey
        ? readCatalogDraftState(queryClient, draftCacheKey)
        : EMPTY_CATALOG_DRAFT_STATE,
    [queryClient, draftCacheKey],
  )
  const draftState = useSyncExternalStore(
    subscribeToDrafts,
    getDraftState,
    () => EMPTY_CATALOG_DRAFT_STATE,
  )
  const [draftRevision, setDraftRevision] = useState(() => uuid())
  const draftRevisionRef = useRef(draftRevision)
  const [selectedCatalogId, setSelectedCatalogId] = useState<string>()
  const [hydratedCatalogId, setHydratedCatalogId] = useState<string>()
  const [date, setDate] = useState<string | undefined>()
  const [hasUnsavedCatalogDraft, setHasUnsavedCatalogDraft] = useState(false)
  const [supportedLocales, setSupportedLocales] = useState<string[]>(['fr'])
  const [defaultLocale, setDefaultLocale] = useState<string>('fr')
  const [contentLocale, setContentLocale] = useState<string>('fr')
  const [firstPageNumber, setFirstPageNumber] = useState<number>(1)
  const [rotorFooterLabel, setRotorFooterLabel] = useState<string | undefined>()
  const [pendingProductSyncCount, setPendingProductSyncCount] = useState(0)
  const productSyncPromisesRef = useRef(new Map<string, Promise<void>>())
  const productTextQueuesRef = useRef(new Map<string, Promise<void>>())
  const productTextNeedsReadRef = useRef(new Set<string>())
  const pendingProductTextRef = useRef(
    new Map<string, Array<{ patch: UpdateBlockPayload }>>(),
  )
  const [isInspectorOpen, setIsInspectorOpen] = useState(false)
  const projectPermissions = useMemo(
    () =>
      resolveProjectPermissions({
        authorization,
        ownership,
        activeOrganizationId: organizationScope.activeOrganizationId,
        project,
      }),
    [authorization, ownership, organizationScope.activeOrganizationId, project],
  )

  const markAsChanged = useCallback(() => {
    const revision = uuid()
    draftRevisionRef.current = revision
    setDraftRevision(revision)
    setHasUnsavedCatalogDraft(true)
  }, [])

  const discardCurrentDraft = useCallback(() => {
    if (draftCacheKey) {
      discardCatalogDraft(queryClient, draftCacheKey, draftRevisionRef.current)
    }
    draftRevisionRef.current = uuid()
    setDraftRevision(draftRevisionRef.current)
    setHasUnsavedCatalogDraft(false)
  }, [draftCacheKey, queryClient])

  useEffect(() => {
    if (!hasUnsavedCatalogDraft) return

    const preventUnload = (event: BeforeUnloadEvent) => {
      event.preventDefault()
      event.returnValue = ''
    }

    window.addEventListener('beforeunload', preventUnload)
    return () => {
      window.removeEventListener('beforeunload', preventUnload)
    }
  }, [hasUnsavedCatalogDraft])

  const beginProductSync = useCallback(() => {
    setPendingProductSyncCount((count) => count + 1)
  }, [])

  const endProductSync = useCallback(() => {
    setPendingProductSyncCount((count) => Math.max(0, count - 1))
  }, [])

  // Fetch catalog data
  const {
    products,
    initialProductsLoaded,
    allProductsLoaded,
    productsError,
    isFetchingProducts,
    catalogs,
    selectedCatalog,
    selectedCatalogId: resolvedSelectedCatalogId,
    hasFetchCatalogs,
    catalogsError,
    isLoadingCatalogs,
    isLoadingSelectedCatalog,
    isFetchingSelectedCatalog,
    isHydratingProducts,
    catalogContext,
    catalogStructureContext,
  } = useCatalogData({
    projectID,
    selectedCatalogId:
      selectedCatalogId === NEW_CATALOG_SELECTION_ID ? null : selectedCatalogId,
    contentLocale,
  })

  const activeSelectedCatalogId = selectedCatalogId ?? resolvedSelectedCatalogId

  const currentCatalog = useMemo(
    () =>
      catalogs.find((catalog) => catalog.id === activeSelectedCatalogId) ??
      selectedCatalog,
    [activeSelectedCatalogId, catalogs, selectedCatalog],
  )

  const currentVersion = currentCatalog?.version ?? 0
  const editorSelectionId = activeSelectedCatalogId ?? NEW_CATALOG_SELECTION_ID
  const baseStamp = catalogBaseStamp(currentCatalog)
  const resumableDraft = projectPermissions.canEditCatalog
    ? draftState.drafts
        .toReversed()
        .find((draft) =>
          draft.baseCatalogId === NEW_CATALOG_SELECTION_ID
            ? draft.baseVersion === 0 &&
              draft.baseStamp === catalogBaseStamp(undefined)
            : catalogs.some(
                (catalog) =>
                  catalog.id === draft.baseCatalogId &&
                  catalog.version === draft.baseVersion &&
                  catalogBaseStamp(catalog) === draft.baseStamp,
              ),
        )
    : undefined

  useEffect(() => {
    if (selectedCatalogId) return
    const draftBaseId = hasUnsavedCatalogDraft
      ? hydratedCatalogId
      : !hydratedCatalogId
        ? resumableDraft?.baseCatalogId
        : undefined
    if (!draftBaseId) return
    // A refetch can add a newer version while this editor still owns its draft.
    setSelectedCatalogId(draftBaseId)
  }, [
    hydratedCatalogId,
    selectedCatalogId,
    resumableDraft,
    hasUnsavedCatalogDraft,
  ])
  const canBootstrapFromPartialProducts = Boolean(resolvedSelectedCatalogId)
  const bootstrapProductsReady = canBootstrapFromPartialProducts
    ? initialProductsLoaded
    : allProductsLoaded
  const hasSelectedCatalogReady = canBootstrapFromPartialProducts
    ? Boolean(selectedCatalog?.id)
    : hasFetchCatalogs
  const hasProjectLanguageSettingsLoaded =
    projectID === 'create' || project !== undefined
  const canHydrateEditor =
    hasFetchCatalogs &&
    !!catalogContext &&
    !!catalogStructureContext &&
    catalogContext.isDone &&
    catalogStructureContext.isDone &&
    hasProjectLanguageSettingsLoaded &&
    bootstrapProductsReady &&
    hasSelectedCatalogReady

  const projectLanguageSettings = useMemo(
    () =>
      resolveProjectLanguageSettings({
        project,
      }),
    [project],
  )
  const effectiveCatalogLanguageSettings = useMemo(
    () =>
      resolveEffectiveCatalogLanguageSettings({
        projectLanguageSettings,
      }),
    [projectLanguageSettings],
  )

  const hydrateCatalogSettings = useCallback(
    (catalog?: Partial<Catalog>) => {
      const languageSettings = resolveEffectiveCatalogLanguageSettings({
        projectLanguageSettings,
      })
      const routeLocale = normalizeCatalogLocale(locale)
      const nextContentLocale =
        routeLocale && languageSettings.supportedLocales.includes(routeLocale)
          ? routeLocale
          : languageSettings.defaultLocale

      setDate(getPublishDateInputValue(catalog))
      setSupportedLocales(languageSettings.supportedLocales)
      setDefaultLocale(languageSettings.defaultLocale)
      setContentLocale(nextContentLocale)
      setFirstPageNumber(getFirstPageNumber(catalog))
      setRotorFooterLabel(getCatalogRotorFooterLabel(catalog))
    },
    [locale, projectLanguageSettings],
  )

  useEffect(() => {
    if (!canHydrateEditor) return
    if (hydratedCatalogId !== editorSelectionId) return

    applyCatalogLanguageSettings({
      languageSettings: effectiveCatalogLanguageSettings,
      supportedLocales,
      defaultLocale,
      contentLocale,
      setSupportedLocales,
      setDefaultLocale,
      setContentLocale,
    })
  }, [
    canHydrateEditor,
    contentLocale,
    defaultLocale,
    effectiveCatalogLanguageSettings,
    editorSelectionId,
    hydratedCatalogId,
    supportedLocales,
  ])

  const contentFallbackLocales = useMemo(
    () =>
      buildContentFallbackLocales({
        activeLocale: contentLocale,
        catalogDefaultLocale: defaultLocale,
        projectDefaultLocale: projectLanguageSettings.defaultLocale,
        teamLanguages: customSettings.languages,
        supportedLocales,
      }),
    [
      contentLocale,
      customSettings.languages,
      defaultLocale,
      projectLanguageSettings.defaultLocale,
      supportedLocales,
    ],
  )

  const newBlocks: CatalogBlock[] = useMemo(() => {
    if (
      !products ||
      !catalogContext?.isDone ||
      !catalogStructureContext?.isDone ||
      !hasFetchCatalogs
    ) {
      return []
    }

    const canBuildBlocks = resolvedSelectedCatalogId
      ? initialProductsLoaded
      : allProductsLoaded
    if (!canBuildBlocks) return []

    return catalogAdapter({
      catalog: selectedCatalog,
      products,
      context: catalogStructureContext,
      allowProductPlaceholders:
        Boolean(resolvedSelectedCatalogId) && isHydratingProducts,
      isHydratingProducts,
    })
  }, [
    allProductsLoaded,
    catalogContext,
    catalogStructureContext,
    hasFetchCatalogs,
    isHydratingProducts,
    initialProductsLoaded,
    products,
    resolvedSelectedCatalogId,
    selectedCatalog,
  ])

  // Manage catalog state
  const {
    blocks,
    updateBlock: rawUpdateBlock,
    reorderBlocks: rawReorderBlocks,
    setBlocks,
    reconcileBlocks,
    getBlock,
    replaceBlockData,
  } = useCatalogState(
    {
      onBlockChange: (_blockId, block, updatedBlock) => {
        if (
          block.type === 'HEADER' ||
          (block.type === 'PRODUCT' &&
            updatedBlock.type === 'PRODUCT' &&
            block.data.detailedSheet !== updatedBlock.data.detailedSheet)
        ) {
          markAsChanged()
        }
      },
      onBlocksReorder: () => {
        markAsChanged()
      },
    },
    catalogStructureContext,
  )

  const blocksRef = useRef<CatalogBlock[]>(blocks)

  useEffect(() => {
    blocksRef.current = blocks
  }, [blocks])

  const updateBlock = useCallback(
    (params: UpdateBlockParams) => {
      if (!projectPermissions.canEditCatalog) return
      rawUpdateBlock(params)
    },
    [projectPermissions.canEditCatalog, rawUpdateBlock],
  )

  const reorderBlocks = useCallback(
    (newBlockIds: string[]) => {
      if (!projectPermissions.canEditCatalog) return
      rawReorderBlocks(newBlockIds)
    },
    [projectPermissions.canEditCatalog, rawReorderBlocks],
  )

  const refreshBlocksByProductID = useCallback(
    (productID: string) => {
      if (!productID || !catalogContext) return

      const targetBlocks = blocksRef.current.filter(
        (block) =>
          block.type === 'PRODUCT' &&
          (block.referenceID === productID ||
            block.data.productID === productID),
      )
      if (!targetBlocks.length) return

      const reduced = getReducedProductFromCache(
        queryClient,
        projectID,
        catalogContext,
        productID,
      )
      if (!reduced) {
        const targetBlockIds = new Set(targetBlocks.map((block) => block.id))
        setBlocks(
          pruneEmptyCatalogSections(
            blocksRef.current.filter((block) => !targetBlockIds.has(block.id)),
          ),
        )
        return
      }

      const pendingText = pendingProductTextRef.current.get(productID) ?? []
      const visibleProduct = pendingText.reduce<AdaptedProduct>(
        (current, { patch }) => ({ ...current, ...patch }) as AdaptedProduct,
        reduced,
      )
      targetBlocks.forEach((block) =>
        replaceBlockData(block.id, visibleProduct),
      )
    },
    [catalogContext, projectID, queryClient, replaceBlockData, setBlocks],
  )

  const syncProductInCatalog = useCallback(
    (productID: string): Promise<void> => {
      if (!productID || !catalogContext) return Promise.resolve()

      const request: Promise<void> = Promise.resolve().then(async () => {
        // A post-write read must not join a query started before that write.
        let occurrences
        try {
          await queryClient.cancelQueries({
            queryKey: ['product', productID],
            exact: true,
          })
          occurrences = (await productByID(productID)) ?? []
        } catch (error) {
          const latest = productSyncPromisesRef.current.get(productID)
          if (latest && latest !== request) return latest
          throw error
        }
        const latest = productSyncPromisesRef.current.get(productID)
        // Resolving a sync promises a reconciled read, including when another
        // field has started a newer read while this response was in flight.
        if (latest && latest !== request) return latest

        reconcileProductOccurrencesInCache(queryClient, productID, occurrences)
        refreshBlocksByProductID(productID)
      })
      productSyncPromisesRef.current.set(productID, request)
      return request
    },
    [catalogContext, queryClient, refreshBlocksByProductID],
  )

  const commitProductTextInCatalog = useCallback(
    ({
      product,
      patch,
      commit,
    }: {
      product: AdaptedProduct
      patch: UpdateBlockPayload
      commit: (base: AdaptedProduct) => Promise<void>
    }) => {
      const productID = product.productID
      if (!productID || !catalogContext || !projectPermissions.canEditCatalog) {
        return Promise.resolve()
      }

      const admittedOccurrences = product.batchedProducts?.length
        ? [...product.batchedProducts]
        : [product]
      const pending = { patch }
      const pendingChanges = pendingProductTextRef.current.get(productID) ?? []
      pendingChanges.push(pending)
      pendingProductTextRef.current.set(productID, pendingChanges)

      const previous = productTextQueuesRef.current.get(productID)
      const queued = (previous ?? Promise.resolve())
        .catch(() => undefined)
        .then(async () => {
          // A failed read after a successful/partial write leaves the cache
          // unsuitable for building another reference snapshot.
          if (productTextNeedsReadRef.current.has(productID)) {
            await syncProductInCatalog(productID)
            productTextNeedsReadRef.current.delete(productID)
          }
          // Use acknowledged occurrences, including each batched occurrence,
          // rather than the product captured by an earlier blur handler.
          const refreshed = getReducedProductFromCache(
            queryClient,
            projectID,
            catalogContext,
            productID,
          )
          const refreshedByID = new Map(
            (refreshed?.batchedProducts?.length
              ? refreshed.batchedProducts
              : refreshed
                ? [refreshed]
                : []
            ).map((occurrence) => [occurrence.id, occurrence]),
          )
          // The product query may also contain occurrences from other projects.
          // Refresh values for this command's targets without widening its IDs.
          const rebased = admittedOccurrences.map(
            (occurrence) => refreshedByID.get(occurrence.id) ?? occurrence,
          )
          const base = product.batchedProducts?.length
            ? { ...product, batchedProducts: rebased }
            : { ...rebased[0], batchedProducts: undefined }
          try {
            productTextNeedsReadRef.current.add(productID)
            await commit(base)
          } catch (error) {
            await syncProductInCatalog(productID)
              .then(() => productTextNeedsReadRef.current.delete(productID))
              .catch(() => undefined)
            throw error
          }
          await syncProductInCatalog(productID)
          productTextNeedsReadRef.current.delete(productID)
        })
        .finally(() => {
          const remaining = (
            pendingProductTextRef.current.get(productID) ?? []
          ).filter((change) => change !== pending)
          if (remaining.length) {
            pendingProductTextRef.current.set(productID, remaining)
          } else {
            pendingProductTextRef.current.delete(productID)
          }
          if (productTextQueuesRef.current.get(productID) === queued) {
            productTextQueuesRef.current.delete(productID)
          }
          refreshBlocksByProductID(productID)
        })
      productTextQueuesRef.current.set(productID, queued)
      return queued
    },
    [
      catalogContext,
      projectID,
      projectPermissions.canEditCatalog,
      queryClient,
      refreshBlocksByProductID,
      syncProductInCatalog,
    ],
  )

  const confirmDiscardAndSwitch = useCallback(
    (catalogId: string) => {
      if (!catalogId || catalogId === activeSelectedCatalogId) return

      discardCurrentDraft()
      setIsInspectorOpen(false)
      setSelectedCatalogId(catalogId)
    },
    [activeSelectedCatalogId, discardCurrentDraft],
  )

  const requestCatalogSwitch = useCallback(
    (catalogId: string) => {
      if (!catalogId || catalogId === activeSelectedCatalogId) return

      if (hasUnsavedCatalogDraft) {
        const shouldDiscard = globalThis.confirm(
          `${tCommon('unsavedChanges')}\n\n${tCommon('unsavedChangesDescription')}`,
        )
        if (!shouldDiscard) return
        confirmDiscardAndSwitch(catalogId)
        return
      }

      setIsInspectorOpen(false)
      setSelectedCatalogId(catalogId)
    },
    [
      confirmDiscardAndSwitch,
      hasUnsavedCatalogDraft,
      activeSelectedCatalogId,
      tCommon,
    ],
  )

  const selectSavedCatalog = useCallback((catalogId: string) => {
    setIsInspectorOpen(false)
    setSelectedCatalogId(catalogId)
    setHydratedCatalogId(catalogId)
  }, [])

  const onSaveSuccess = useCallback(
    (catalogId: string) => {
      if (draftRevisionRef.current !== draftRevision) return
      if (
        draftCacheKey &&
        hasUnsavedCatalogDraft &&
        !completeCatalogDraft(
          queryClient,
          draftCacheKey,
          draftRevision,
          catalogId,
        )
      )
        return
      setHasUnsavedCatalogDraft(false)
      selectSavedCatalog(catalogId)
    },
    [
      draftRevision,
      draftCacheKey,
      hasUnsavedCatalogDraft,
      queryClient,
      selectSavedCatalog,
    ],
  )

  useEffect(() => {
    const saved = draftState.lastSaved
    if (!saved || saved.revision !== draftRevisionRef.current) return
    setHasUnsavedCatalogDraft(false)
    selectSavedCatalog(saved.catalogId)
  }, [draftState.lastSaved, selectSavedCatalog])

  // Version control
  const { isSaving, handleSave: saveHandler } = useCatalogVersioning({
    catalogs,
    projectID,
    blocks,
    catalogContext: catalogStructureContext,
    setHasUnsavedCatalogDraft,
    selectSavedCatalog,
    date,
    supportedLocales,
    defaultLocale,
    firstPageNumber,
    rotorFooterLabel,
    projectPermissions,
    onSaveSuccess,
  })

  // Navigation
  const {
    selectedBlockId,
    selectBlock,
    clearSelectedBlock,
    categoryRefs,
    handleNavigate,
    setVirtualizedScrollRef,
  } = useCatalogNavigation(blocks)

  const openInspector = useCallback(() => {
    setIsInspectorOpen(true)
  }, [])

  const closeInspector = useCallback(() => {
    setIsInspectorOpen(false)
    clearSelectedBlock()
  }, [clearSelectedBlock])

  const toggleInspector = useCallback(() => {
    setIsInspectorOpen((current) => !current)
  }, [])

  // Zoom
  const { zoomRef, zoomIn, zoomOut, currentZoomLevel } = useZoom()

  // Keyboard shortcuts
  useCatalogKeyboardShortcuts({
    onSave: saveHandler,
    onDismissSelection: closeInspector,
    hasLoaded: Boolean(hydratedCatalogId),
    hasUnsavedChanges: currentVersion === 0 || hasUnsavedCatalogDraft,
    hasSelection: Boolean(selectedBlockId),
  })

  // Initial hydration and explicit catalog switches only.
  useEffect(() => {
    if (!canHydrateEditor) return

    const hasSelectionChanged = hydratedCatalogId !== editorSelectionId
    if (!hasSelectionChanged) return

    if (
      !selectedCatalogId &&
      resumableDraft &&
      resumableDraft.baseCatalogId !== editorSelectionId
    )
      return

    const retained = projectPermissions.canEditCatalog
      ? draftState.drafts.find(
          (draft) =>
            draft.baseCatalogId === editorSelectionId &&
            draft.baseVersion === currentVersion &&
            draft.baseStamp === baseStamp,
        )
      : undefined
    if (retained) {
      setBlocks(
        catalogAdapter({
          catalog: { ...currentCatalog, content: [retained.content] },
          products,
          context: catalogStructureContext,
          allowProductPlaceholders: isHydratingProducts,
          isHydratingProducts,
        }),
      )
      setDate(retained.date)
      setSupportedLocales(retained.supportedLocales)
      setDefaultLocale(retained.defaultLocale)
      setContentLocale(retained.contentLocale)
      setFirstPageNumber(retained.firstPageNumber)
      setRotorFooterLabel(retained.rotorFooterLabel)
      draftRevisionRef.current = retained.revision
      setDraftRevision(retained.revision)
      setHasUnsavedCatalogDraft(true)
      setHydratedCatalogId(editorSelectionId)
      return
    }

    setBlocks(newBlocks)
    hydrateCatalogSettings(currentCatalog)
    setHydratedCatalogId(editorSelectionId)
  }, [
    canHydrateEditor,
    currentCatalog,
    editorSelectionId,
    hydrateCatalogSettings,
    hydratedCatalogId,
    newBlocks,
    setBlocks,
    selectedCatalogId,
    resumableDraft,
    projectPermissions.canEditCatalog,
    draftState.drafts,
    currentVersion,
    baseStamp,
    products,
    catalogStructureContext,
    isHydratingProducts,
  ])

  useEffect(() => {
    if (
      !draftCacheKey ||
      !hasUnsavedCatalogDraft ||
      !projectPermissions.canEditCatalog ||
      !canHydrateEditor ||
      hydratedCatalogId !== editorSelectionId
    )
      return
    retainCatalogDraft(queryClient, draftCacheKey, {
      baseCatalogId: editorSelectionId,
      baseVersion: currentVersion,
      baseStamp,
      revision: draftRevision,
      content: { version: '1.0', blocks: serializeCatalogBlocks(blocks) },
      date,
      supportedLocales,
      defaultLocale,
      contentLocale,
      firstPageNumber,
      rotorFooterLabel,
    })
  }, [
    draftCacheKey,
    hasUnsavedCatalogDraft,
    projectPermissions.canEditCatalog,
    canHydrateEditor,
    hydratedCatalogId,
    editorSelectionId,
    queryClient,
    baseStamp,
    currentVersion,
    draftRevision,
    blocks,
    date,
    supportedLocales,
    defaultLocale,
    contentLocale,
    firstPageNumber,
    rotorFooterLabel,
  ])

  const syncHydratedProductBlocks = useCallback(
    (blocksToSync: CatalogBlock[], preserveBlockIds: Set<string>) => {
      blocksToSync.forEach((nextBlock) => {
        if (
          nextBlock.type !== 'PRODUCT' ||
          preserveBlockIds.has(nextBlock.id)
        ) {
          return
        }

        const currentBlock = getBlock(nextBlock.id)
        if (!currentBlock || currentBlock.type !== 'PRODUCT') return

        const nextData = {
          ...nextBlock.data,
          detailedSheet: currentBlock.data?.detailedSheet,
        }

        if (JSON.stringify(currentBlock.data) === JSON.stringify(nextData)) {
          return
        }

        replaceBlockData(nextBlock.id, nextData)
      })
    },
    [getBlock, replaceBlockData],
  )

  useEffect(() => {
    if (!canHydrateEditor) return
    if (hydratedCatalogId !== editorSelectionId) return
    if (!newBlocks.length) return

    const preserveBlockIds = selectedBlockId
      ? new Set([selectedBlockId])
      : new Set<string>()
    newBlocks.forEach((block) => {
      if (
        block.type === 'PRODUCT' &&
        pendingProductTextRef.current.has(block.data.productID)
      ) {
        preserveBlockIds.add(block.id)
      }
    })
    const shouldPauseStructuralHydration = hasUnsavedCatalogDraft

    if (shouldPauseStructuralHydration) {
      syncHydratedProductBlocks(newBlocks, preserveBlockIds)
      return
    }

    reconcileBlocks(newBlocks, { preserveBlockIds })
  }, [
    canHydrateEditor,
    editorSelectionId,
    hasUnsavedCatalogDraft,
    hydratedCatalogId,
    newBlocks,
    reconcileBlocks,
    selectedBlockId,
    syncHydratedProductBlocks,
  ])

  useEffect(() => {
    if (!selectedBlockId) return
    if (blocks.some((block) => block.id === selectedBlockId)) return

    clearSelectedBlock()
  }, [blocks, clearSelectedBlock, selectedBlockId])

  const handleSort = useCallback(() => {
    if (!projectPermissions.canEditCatalog) return

    const sortedBlocks = sortBlocksByLotAndCategory(
      blocks,
      catalogStructureContext,
    )
    setBlocks(sortedBlocks)
    markAsChanged()
  }, [
    blocks,
    catalogStructureContext,
    markAsChanged,
    projectPermissions.canEditCatalog,
    setBlocks,
  ])

  const hasError = !!productsError || !!catalogsError
  const isInitializing =
    !hydratedCatalogId &&
    !hasError &&
    (!hasFetchCatalogs ||
      !catalogContext?.isDone ||
      !catalogStructureContext?.isDone ||
      !hasProjectLanguageSettingsLoaded ||
      !bootstrapProductsReady ||
      (canBootstrapFromPartialProducts &&
        (!selectedCatalog ||
          isLoadingSelectedCatalog ||
          isFetchingSelectedCatalog ||
          isLoadingCatalogs)))
  const hasLoaded = Boolean(hydratedCatalogId) && !hasError && !isInitializing
  const hasPendingProductSync = pendingProductSyncCount > 0

  const value = useMemo(
    () => ({
      // Data
      catalogs,
      selectedCatalogId: activeSelectedCatalogId,
      blocks,
      hasLoaded,
      hasError,
      isInitializing,
      isHydratingProducts:
        isHydratingProducts || (allProductsLoaded ? false : isFetchingProducts),

      // Version control
      currentVersion,
      hasUnsavedCatalogDraft,
      hasPendingProductSync,
      isSaving,

      // Options management
      date,
      setDate,
      supportedLocales,
      defaultLocale,
      contentLocale,
      setContentLocale,
      contentFallbackLocales,
      projectDefaultLocale: projectLanguageSettings.defaultLocale,
      firstPageNumber,
      setFirstPageNumber,

      rotorFooterLabel,
      setRotorFooterLabel,

      // Block operations
      updateBlock,
      reorderBlocks,
      handleSort,

      // Navigation
      selectedBlockId,
      selectBlock,
      clearSelectedBlock,
      categoryRefs,
      handleNavigate,
      setVirtualizedScrollRef,
      isInspectorOpen,
      openInspector,
      closeInspector,
      toggleInspector,
      requestCatalogSwitch,
      confirmDiscardAndSwitch,

      // Zoom
      zoomRef,
      zoomIn,
      zoomOut,
      currentZoomLevel,

      // Actions
      handleSave: saveHandler,
      markAsChanged,
      refreshBlocksByProductID,
      syncProductInCatalog,
      commitProductTextInCatalog,
      beginProductSync,
      endProductSync,

      // Context
      catalogContext,
      catalogStructureContext,
      projectID,
      locale: contentLocale,
      projectPermissions,
    }),
    [
      allProductsLoaded,
      beginProductSync,
      blocks,
      catalogs,
      catalogContext,
      catalogStructureContext,
      categoryRefs,
      confirmDiscardAndSwitch,
      currentVersion,
      currentZoomLevel,
      contentLocale,
      contentFallbackLocales,
      date,
      defaultLocale,
      endProductSync,
      firstPageNumber,
      handleNavigate,
      handleSort,
      hasError,
      hasLoaded,
      hasPendingProductSync,
      hasUnsavedCatalogDraft,
      isFetchingProducts,
      isHydratingProducts,
      isInspectorOpen,
      isInitializing,
      isSaving,
      markAsChanged,
      projectLanguageSettings.defaultLocale,
      projectID,
      projectPermissions,
      refreshBlocksByProductID,
      reorderBlocks,
      requestCatalogSwitch,
      openInspector,
      closeInspector,
      toggleInspector,
      rotorFooterLabel,
      saveHandler,
      clearSelectedBlock,
      selectBlock,
      selectedBlockId,
      activeSelectedCatalogId,
      setDate,
      setContentLocale,
      setFirstPageNumber,
      setRotorFooterLabel,
      setVirtualizedScrollRef,
      supportedLocales,
      syncProductInCatalog,
      commitProductTextInCatalog,
      updateBlock,
      zoomIn,
      zoomOut,
      zoomRef,
    ],
  )

  return (
    <CatalogEditorContext.Provider value={value}>
      {children}
    </CatalogEditorContext.Provider>
  )
}

export const useCatalogEditor = () => {
  const context = useContext(CatalogEditorContext)
  if (!context) {
    throw new Error(
      'useCatalogEditor must be used within a CatalogEditorProvider',
    )
  }
  return context
}
